import { forwardRef } from "react";

interface ViralPosterCardProps {
  message: string;
  score: number;
  pattern: string;
  interpretation: string;
  confidence?: string;
}

/**
 * Creator Card — social-first, hook-driven layout optimized for TikTok/IG engagement.
 * Visually distinct from PatternFirstCard: bold color blocks, large hook question,
 * engagement prompt footer, more opinionated styling.
 */
const ViralPosterCard = forwardRef<HTMLDivElement, ViralPosterCardProps>(
  ({ message, score, pattern, interpretation, confidence }, ref) => {
    const isHigh = score >= 7;
    const isMod = score >= 4;

    const accentColor = isHigh ? "#ef4444" : isMod ? "#f59e0b" : "#64748b";
    const accentGlow = isHigh ? "#ef444440" : isMod ? "#f59e0b30" : "#64748b20";
    const verdictText = isHigh
      ? "This is manipulation."
      : isMod
      ? "This is suspicious."
      : "Likely not manipulation.";

    return (
      <div
        ref={ref}
        style={{
          position: "absolute",
          left: "-9999px",
          top: "-9999px",
          width: 1080,
          height: 1920,
          background: "#050505",
          fontFamily: "'Inter', system-ui, sans-serif",
          boxSizing: "border-box",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Full-width accent strip at top */}
        <div
          style={{
            height: 6,
            background: `linear-gradient(90deg, ${accentColor}, ${accentColor}60, transparent)`,
            flexShrink: 0,
          }}
        />

        {/* Content area */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            padding: "72px 80px 56px",
          }}
        >
          {/* Hook question — BIG, attention-grabbing */}
          <div
            style={{
              fontSize: 42,
              fontWeight: 800,
              lineHeight: 1.15,
              color: "#ffffff",
              textAlign: "center",
              marginBottom: 16,
              flexShrink: 0,
            }}
          >
            Have you ever heard this? 👇
          </div>

          {/* Subhook */}
          <div
            style={{
              fontSize: 16,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.3)",
              fontFamily: "'JetBrains Mono', monospace",
              textAlign: "center",
              marginBottom: 56,
              flexShrink: 0,
            }}
          >
            AI Forensic Breakdown
          </div>

          {/* Quoted message in accent-bordered block */}
          <div
            style={{
              position: "relative",
              padding: "40px 48px",
              borderRadius: 16,
              background: "rgba(255,255,255,0.03)",
              border: `1px solid ${accentColor}30`,
              marginBottom: 56,
              flexShrink: 0,
            }}
          >
            {/* Large quote mark */}
            <div
              style={{
                position: "absolute",
                top: 12,
                left: 24,
                fontSize: 72,
                lineHeight: 1,
                color: `${accentColor}25`,
                fontFamily: "Georgia, serif",
              }}
            >
              "
            </div>
            <div
              style={{
                fontSize: 30,
                lineHeight: "46px",
                color: "rgba(255,255,255,0.9)",
                fontWeight: 400,
                fontStyle: "italic",
                textAlign: "center",
                wordBreak: "break-word",
              }}
            >
              {message.slice(0, 200)}
            </div>
          </div>

          {/* Verdict — the punchline */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 0,
            }}
          >
            {/* Score — big, glowing */}
            <div
              style={{
                fontSize: 96,
                fontWeight: 900,
                color: accentColor,
                lineHeight: 1,
                fontFamily: "'JetBrains Mono', monospace",
                textShadow: `0 0 60px ${accentGlow}`,
                marginBottom: 12,
              }}
            >
              {score.toFixed(1)}
            </div>

            {/* Verdict label */}
            <div
              style={{
                fontSize: 14,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.3)",
                fontFamily: "'JetBrains Mono', monospace",
                marginBottom: 36,
              }}
            >
              Manipulation Score
            </div>

            {/* Bold verdict statement */}
            <div
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: accentColor,
                textAlign: "center",
                marginBottom: 24,
                textShadow: `0 0 20px ${accentGlow}`,
              }}
            >
              {verdictText}
            </div>

            {/* Pattern pill */}
            <div
              style={{
                display: "inline-flex",
                padding: "10px 28px",
                borderRadius: 32,
                background: `${accentColor}12`,
                border: `1px solid ${accentColor}30`,
                marginBottom: 24,
              }}
            >
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: accentColor,
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {pattern}
              </span>
            </div>

            {/* Interpretation */}
            <div
              style={{
                fontSize: 22,
                lineHeight: "36px",
                color: "rgba(255,255,255,0.6)",
                textAlign: "center",
                maxWidth: 780,
                fontWeight: 400,
              }}
            >
              {interpretation}
            </div>
          </div>

          {/* Engagement CTA footer */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 20,
              flexShrink: 0,
              paddingTop: 32,
            }}
          >
            {/* Divider line */}
            <div
              style={{
                width: 160,
                height: 1,
                background: "rgba(255,255,255,0.08)",
              }}
            />

            {/* Engagement hook */}
            <div
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: "rgba(255,255,255,0.5)",
                textAlign: "center",
              }}
            >
              Agree with this score? 🤔
            </div>

            {/* Branding */}
            <div
              style={{
                fontSize: 14,
                letterSpacing: "0.2em",
                color: "rgba(255,255,255,0.15)",
                fontFamily: "'JetBrains Mono', monospace",
                textTransform: "uppercase",
              }}
            >
              Analyze yours → GreyRockAI.app®
            </div>
          </div>
        </div>
      </div>
    );
  }
);

ViralPosterCard.displayName = "ViralPosterCard";
export default ViralPosterCard;
